"use client"

import { useMemo, useState } from "react"
import { Plus, X, Edit, Trash2, ChevronRight, ChevronDown, Save, PlusCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import EmptyCategory from "@/assets/empty_categoery.png"
import type { Category } from "@/types/facility/category"
import {
    useGetCategoryQuery,
    useCreateCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
} from "@/services/facilityCategoryApis"
import { useForm } from "react-hook-form"
import { useToast } from "@/components/ui/use-toast"
import { ConfirmationDialog } from "@/components/ui/common/confirmation-dailog"
import useDocumentTitle from "@/components/ui/common/document-title"
import { useSelector } from "react-redux"
import { RootState } from "@/redux/store"
import { ErrorType } from "@/types"
// import { RootState } from "@/"

type CategoryFormData = {
    name: string
    parent_category_id: number
}

export function FacilityCategory() {
    useDocumentTitle("Facility Category");
    const { toast } = useToast()
    const orgId = useSelector(
        (state: RootState) => state.auth.userInfo?.user?.org_id
    ) || 0;

    const [isOpen, setIsOpen] = useState(false)
    const [title, setTitle] = useState("")
    const [message, setMessage] = useState("")
    const [confirmTitle, setConfirmTitle] = useState("")

    const { data: categoryData, isLoading } = useGetCategoryQuery({ query: "load_subcategories=true&sort_key=id&sort_order=desc", org_id: orgId })
    const [createCategory] = useCreateCategoryMutation()
    const [updateCategory] = useUpdateCategoryMutation()
    const [deleteCategory] = useDeleteCategoryMutation()

    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({})
    const [editingCategory, setEditingCategory] = useState<number | null>(null)
    const [editingSubcategory, setEditingSubcategory] = useState<{ categoryId: number; subcategoryId: number } | null>(
        null,
    )
    const [addingSubcategoryTo, setAddingSubcategoryTo] = useState<number | null>(null)
    const [isAddingCategory, setIsAddingCategory] = useState(false)

    const form = useForm<CategoryFormData>({
        mode: "all",
        defaultValues: {
            name: "",
            parent_category_id: 0,
        },
    })

    const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = form
    const currentName = watch("name")

    const toggleCategory = (categoryId: number) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }))
    }

    const onSubmitCategory = async (data: CategoryFormData) => {
        if (data.name.trim() === "") {
            toast({
                title: "Name is required",
                variant: "destructive",
            })
            return
        }
        const payload = {
            ...data,
            name: data.name.trim(),
        }

        try {
            if (editingCategory) {
                await updateCategory({
                    id: editingCategory,
                    ...payload,
                }).unwrap()
                setEditingCategory(null)
                toast({
                    title: "Category updated successfully",
                    variant: "success",
                })
            } else if (editingSubcategory) {
                await updateCategory({
                    id: editingSubcategory.subcategoryId,
                    ...payload,
                }).unwrap()
                setEditingSubcategory(null)
                toast({
                    title: "Subcategory updated successfully",
                    variant: "success",
                })
            } else if (addingSubcategoryTo) {
                await createCategory({
                    ...payload,
                    parent_category_id: addingSubcategoryTo,
                }).unwrap()
                setAddingSubcategoryTo(null)
                toast({
                    title: "Subcategory created successfully",
                    variant: "success",
                })
            } else {
                await createCategory(payload).unwrap()
                setIsAddingCategory(false)
                toast({
                    title: "Category created successfully",
                    variant: "success",
                })
            }
            reset()
        } catch (error) {
            console.error("Error", { error });
            if (error && typeof error === "object" && "data" in error) {
                const typedError = error as ErrorType;
                toast({
                    variant: "destructive",
                    title: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
                });
            }
        }
    }

    const handleDeleteCategory = async (categoryId: number, parentCategoryId: number | null) => {
        setIsOpen(true)
        setSelectedCategoryId(categoryId)
        setSelectedParentCategoryId(parentCategoryId)
        setTitle(`Delete ${parentCategoryId == null ? "Category" : "Subcategory"}`)
        setMessage(`Are you sure you want to delete this ${parentCategoryId == null ? "category" : "subcategory"}? This action cannot be undone.`)
        setConfirmTitle("Delete")
    }

    const onClose = () => {
        setIsOpen(false)
        setSelectedCategoryId(null)
        setSelectedParentCategoryId(null)
    }

    const onConfirm = async () => {
        if (!selectedCategoryId) return

        const isSubcategory = selectedParentCategoryId != null

        try {
            await deleteCategory(selectedCategoryId).unwrap()
            toast({
                title: isSubcategory ? "Subcategory deleted successfully" : "Category deleted successfully",
                variant: "success",
            })
        } catch (error) {
            toast({
                title: isSubcategory ? "Failed to delete subcategory" : "Failed to delete category",
                variant: "destructive",
            })
        }
        setIsOpen(false)
        setSelectedCategoryId(null)
        setSelectedParentCategoryId(null)
    }

    // Add these state variables at the top of your component
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
    const [selectedParentCategoryId, setSelectedParentCategoryId] = useState<number | null>(null)

    const handleEditCategory = (category: Category) => {
        setEditingCategory(category.id as number)
        setValue("name", category.name)
        setValue("parent_category_id", category.parent_category_id || 0)
    }

    const handleEditSubcategory = (categoryId: number, subcategory: Category) => {
        setEditingSubcategory({ categoryId, subcategoryId: subcategory.id as number })
        setValue("name", subcategory.name)
        setValue("parent_category_id", subcategory.parent_category_id || 0)
    }

    const handleCancelEdit = () => {
        setEditingCategory(null)
        setEditingSubcategory(null)
        setAddingSubcategoryTo(null)
        setIsAddingCategory(false)
        reset()
    }

    const categories = useMemo(() => {
        return Array.isArray(categoryData?.data) ? categoryData?.data : []
    }, [categoryData])

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        )
    }

    if (categories.length === 0 && !isAddingCategory) {
        return (
            <div className="p-5">
                <Card className="w-full max-w-3xl mx-auto">
                    <CardHeader className="flex flex-row items-center justify-between py-3">
                        <CardTitle className="text-lg font-medium">Categories</CardTitle>
                        <Button
                            onClick={() => setIsAddingCategory(true)}
                            size="sm"
                            className="bg-primary hover:bg-primary/90 transition-colors"
                        >
                            <Plus className="mr-1 h-4 w-4" /> Add Category
                        </Button>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                        <img
                            src={EmptyCategory}
                            alt="No categories"
                            width={200}
                            height={200}
                            className="mb-6 opacity-80"
                        />
                        <h3 className="text-base mb-2">No Categories Yet</h3>

                    </CardContent>
                </Card>
            </div>
        )
    }

    return (
        <div className="p-5">
            <Card className="w-full max-w-3xl mx-auto">
                <CardHeader className="flex flex-row items-center justify-between py-3">
                    <CardTitle className="text-lg font-medium">Categories</CardTitle>
                    {!isAddingCategory && !editingCategory && !editingSubcategory && !addingSubcategoryTo && (
                        <Button onClick={() => setIsAddingCategory(true)} size="sm" className="bg-primary hover:bg-primary/90">
                            <Plus className="mr-1 h-4 w-4" /> Add Category
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {/* Add new category form */}
                    {isAddingCategory && (
                        <form
                            onSubmit={handleSubmit(onSubmitCategory)}
                            className="mb-4 p-2 border rounded-md bg-muted/20"
                        >
                            <div className="flex items-center gap-2">
                                <Input placeholder="Category name" {...register("name", {
                                    required: "Required", maxLength: {
                                        value: 255,
                                        message: "Category name must be less than 255 characters",
                                    }
                                })} className="flex-1" autoFocus />
                                <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90">
                                    <Save className="h-4 w-4" />
                                </Button>
                                <Button onClick={handleCancelEdit} size="sm" variant="ghost">
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                            {errors.name && <p className="text-destructive text-xs mt-2">{errors.name.message}</p>}
                        </form>
                    )}

                    {/* Categories list */}
                    <div className="space-y-2">
                        {categories
                            .filter((cat) => !cat.parent_category_id)
                            .map((category) => (
                                <div key={category.id} className="border rounded-md overflow-hidden">
                                    <div className="flex items-center justify-between p-3 bg-muted/10">
                                        <div className="flex items-center gap-2 flex-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="p-0 h-8 w-8"
                                                onClick={() => toggleCategory(category.id as number)}
                                            >
                                                {expandedCategories[category.id as number] ? (
                                                    <ChevronDown className="h-4 w-4" />
                                                ) : (
                                                    <ChevronRight className="h-4 w-4" />
                                                )}
                                            </Button>

                                            {editingCategory === category.id ? (
                                                <form onSubmit={handleSubmit(onSubmitCategory)} className="w-full">
                                                    <div className="flex items-center gap-2 flex-1 ">
                                                        <Input {...register("name", {
                                                            required: "Required", maxLength: {
                                                                value: 255,
                                                                message: "Category name must be less than 255 characters",
                                                            }
                                                        })} className="h-8" autoFocus />
                                                        <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90 h-8">
                                                            <Save className="h-4 w-4" />
                                                        </Button>
                                                        <Button onClick={handleCancelEdit} size="sm" variant="ghost" className="h-8">
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                    {errors.name && <p className="text-destructive text-xs mt-2">{errors.name.message}</p>}
                                                </form>
                                            ) : (
                                                <div className="font-medium break-all">{category.name}</div>
                                            )}
                                        </div>

                                        {editingCategory !== category.id && (
                                            <div className="flex items-center gap-1">
                                                <Badge className="bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/30">
                                                    {categories.filter((sub) => sub.parent_category_id === category.id).length}
                                                </Badge>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0"
                                                    onClick={() => handleEditCategory(category)}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                                    onClick={() => handleDeleteCategory(category.id as number, category.parent_category_id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 w-8 p-0 text-primary hover:text-primary/90"
                                                    onClick={() => {
                                                        setAddingSubcategoryTo(category.id as number)
                                                        setExpandedCategories((prev) => ({
                                                            ...prev,
                                                            [category.id as number]: true,
                                                        }))
                                                    }}
                                                >
                                                    <PlusCircle className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Subcategories */}
                                    <div
                                        className={cn(
                                            "overflow-hidden transition-all duration-200",
                                            expandedCategories[category.id as number] ? "max-h-[1000px]" : "max-h-0",
                                        )}
                                    >
                                        {/* Add new subcategory form */}
                                        {addingSubcategoryTo === category.id && (
                                            <form
                                                onSubmit={handleSubmit(onSubmitCategory)}
                                                className="p-2 mx-3 my-2 border rounded-md bg-muted/10 "
                                            >
                                                <div className="flex items-center gap-2 flex-1">
                                                    <Input
                                                        placeholder="Subcategory name"
                                                        {...register("name", {
                                                            required: "Required", maxLength: {
                                                                value: 255,
                                                                message: "Subcategory name must be less than 255 characters",
                                                            }
                                                        })}
                                                        className="flex-1 h-8"
                                                        autoFocus
                                                    />
                                                    <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90 h-8">
                                                        <Save className="h-4 w-4" />
                                                    </Button>
                                                    <Button onClick={handleCancelEdit} size="sm" variant="ghost" className="h-8">
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                                {errors.name && <p className="text-destructive text-xs mt-2">{errors.name.message}</p>}
                                            </form>
                                        )}

                                        {/* Subcategories list */}
                                        <div className="divide-y">
                                            {categories
                                                .filter((sub) => sub.parent_category_id === category.id)
                                                .map((subcategory) => (
                                                    <div
                                                        key={subcategory.id}
                                                        className="flex items-center justify-between p-2 pl-10 hover:bg-muted/5"
                                                    >
                                                        {editingSubcategory?.categoryId === category.id &&
                                                            editingSubcategory?.subcategoryId === subcategory.id ? (
                                                            <form onSubmit={handleSubmit(onSubmitCategory)} className="w-full">
                                                                <div className="flex items-center gap-2 flex-1">
                                                                    <Input {...register("name", {
                                                                        required: "Required", maxLength: {
                                                                            value: 255,
                                                                            message: "Category name must be less than 255 characters",
                                                                        }
                                                                    })} className="flex-1 h-8" autoFocus />
                                                                    <Button type="submit" size="sm" className="bg-primary hover:bg-primary/90 h-8">
                                                                        <Save className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button onClick={handleCancelEdit} size="sm" variant="ghost" className="h-8">
                                                                        <X className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                                {errors.name && <p className="text-destructive text-xs mt-2">{errors.name.message}</p>}
                                                            </form>
                                                        ) : (
                                                            <>
                                                                <span className="break-all">{subcategory.name}</span>
                                                                <div className="flex items-center gap-1">
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0"
                                                                        onClick={() => handleEditSubcategory(category.id as number, subcategory)}
                                                                    >
                                                                        <Edit className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                                                        onClick={() => handleDeleteCategory(subcategory.id as number, subcategory.parent_category_id)}
                                                                    >
                                                                        <Trash2 className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                ))}

                                            {/* Empty state for subcategories */}
                                            {categories.filter((sub) => sub.parent_category_id === category.id).length === 0 && (
                                                <div className="p-2 pl-10 text-sm text-muted-foreground italic">No subcategories yet</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </CardContent>
            </Card>

            <ConfirmationDialog
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={onConfirm}
                title={title}
                message={message}
                confirmTitle={confirmTitle}
            />
        </div>
    )
}


